import { Box, useMediaQuery, useTheme } from '@mui/material'
import MasonryTile from '../../components/Masonry/MasonryTile'
import { DateRange } from '@mui/icons-material'
import { StyledScheduleStickyTitle } from '../../styles/ScheduleViewStyles'
import MasonryTileTitle from '../../components/Masonry/MasonryTileTitle'
import MonthPicker from '../../components/MonthPicker/ReduxMonthPicker'
import { useTranslation } from 'react-i18next'
import FilterMenteesMenuPopup from '../../components/Popup/LeaderView/FilterMenteesMenuPopup'
import { RequestProps } from '../../store/types'
import React, { useEffect, useState } from 'react'
import defaultProps from '../../config/defaultProps'
import LeaderMenteesPlanMode from '../../store/types/enums/LeaderMenteesPlanMode'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsManagement } from '../../store/redux/reducers/currentUserSlice'
import { useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import { getSelectedDate } from '../../store/redux/reducers/monthPickerSlice'
import dayjs from 'dayjs'
import LeaderCalendar from './LeaderCalendar'
import ToggleCalendarButton from '../../components/Button/ToggleCalendarButton'

type MasonryTileCalendarProps = {
  isWeeklyRange: boolean
  setIsWeeklyRange: React.Dispatch<React.SetStateAction<boolean>>
}

const MasonryTileCalendar = ({ isWeeklyRange, setIsWeeklyRange }: MasonryTileCalendarProps) => {
  const theme = useTheme()
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isDownXs = useMediaQuery(theme.breakpoints.down(380))

  const { t } = useTranslation()
  const currentUser = useSelector(getCurrentUser)
  const isManagement = useSelector(getIsManagement)

  const { data: menteeResponse, isLoading: isMenteesLoading } = useGetLightUsersQuery({
    page: 0,
    size: 500,
    'leaders.id': currentUser?.id,
  })

  const { data: restOfEmployeesResponse, isLoading: isEmployeesLoading } = useGetLightUsersQuery(
    {
      page: 0,
      size: 500,
    },
    { skip: !isManagement }
  )

  useEffect(() => {
    if (menteeResponse) {
      setMenteesPlanProps(prevProps => ({
        ...prevProps,
        menteesIds: menteeResponse.content.map(mentee => mentee.id),
      }))
    }
  }, [menteeResponse])

  const selectedDate = useSelector(getSelectedDate)

  const [menteesPlanProps, setMenteesPlanProps] = useState<RequestProps>({
    ...defaultProps,
    menteesIds: [],
    mode: LeaderMenteesPlanMode.WEEK,
    date: selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : dayjs(new Date()).format('YYYY-MM-DD'),
  })

  const changeMode = () => {
    if (isWeeklyRange)
      setMenteesPlanProps((prevProps: any) => ({
        ...prevProps,
        mode: LeaderMenteesPlanMode.DAY,
      }))
    else
      setMenteesPlanProps((prevProps: any) => ({
        ...prevProps,
        mode: LeaderMenteesPlanMode.WEEK,
      }))

    setIsWeeklyRange(!isWeeklyRange)
  }

  useEffect(() => {
    setMenteesPlanProps((prevProps: any) => ({
      ...prevProps,
      date: dayjs(selectedDate).format('YYYY-MM-DD'),
    }))
  }, [selectedDate, setMenteesPlanProps])

  return (
    <MasonryTile sx={{ width: '100%', height: 'auto' }}>
      <StyledScheduleStickyTitle sx={{ height: 'auto' }}>
        {isDownMd ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', flexDirection: 'column', gap: 1 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              flexDirection: isDownXs ? 'column' : 'row',
              gap: isDownSm ? 1 : 0
            }}>
              <MasonryTileTitle
                sx={{ width: '100%' }}
                title={isWeeklyRange ? t('page.leaderView.weeklyCalendar') : t('page.leaderView.dailyCalendar')}
                icon={DateRange}
              />
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', gap: 1, alignItems: 'center' }}>
                <FilterMenteesMenuPopup
                  requestProps={menteesPlanProps}
                  setRequestProps={setMenteesPlanProps}
                  allMentees={menteeResponse?.content}
                  isMenteesLoading={isMenteesLoading}
                  restEmployees={restOfEmployeesResponse?.content}
                  isEmployeesLoading={isEmployeesLoading}
                />
                <ToggleCalendarButton isWeeklyRange={isWeeklyRange} changeMode={changeMode} />
              </Box>
            </Box>

            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <MonthPicker isWeeklyRange={isWeeklyRange} isDailyRange={!isWeeklyRange} />
            </Box>

          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <MasonryTileTitle
              sx={{ width: '35%' }}
              title={isWeeklyRange ? t('page.leaderView.weeklyCalendar') : t('page.leaderView.dailyCalendar')}
              icon={DateRange}
            />
            <Box sx={{ width: '40%', display: 'flex', justifyContent: 'center' }}>
              <MonthPicker isWeeklyRange={isWeeklyRange} isDailyRange={!isWeeklyRange} />
            </Box>
            <Box sx={{ width: '35%', display: 'flex', justifyContent: 'end', gap: 1, alignItems: 'center' }}>
              <FilterMenteesMenuPopup
                requestProps={menteesPlanProps}
                setRequestProps={setMenteesPlanProps}
                allMentees={menteeResponse?.content}
                isMenteesLoading={isMenteesLoading}
                restEmployees={restOfEmployeesResponse?.content}
                isEmployeesLoading={isEmployeesLoading}
              />
              <ToggleCalendarButton isWeeklyRange={isWeeklyRange} changeMode={changeMode} />
            </Box>
          </Box>
        )}
      </StyledScheduleStickyTitle>

      {!isMenteesLoading && (
        <LeaderCalendar isWeeklyRange={isWeeklyRange} selectedDate={selectedDate} menteesPlanProps={menteesPlanProps} />
      )}
    </MasonryTile>
  )
}

export default MasonryTileCalendar